import { Menu, MenuItem, SubMenu, Sidebar } from 'react-pro-sidebar';
import {
	FaSignOutAlt,
	FaFileImport,
	FaRegHeart,
	FaUniversity,
	FaCar,
	FaBuilding,
	FaUserTie,
	FaAddressBook,
	FaFilePdf,
	FaMagic,
	FaUsers,
	FaWrench,
	FaHeadset,
} from 'react-icons/fa';
import { BiSolidBusiness } from 'react-icons/bi';
import { SiOnlyoffice } from 'react-icons/si';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { Markets } from '../../types';
import Icon from '../Icon/Icon';
import './SideMenu.scss';
import 'tippy.js/dist/tippy.css';
import { AFFILIATION_KEY, GTMsendEvent, USER_ROLE_KEY } from '../../utils/trackingGA4';
import { useCookies } from 'react-cookie';
import { isMarket } from '../../utils/markets';
import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { RoleEnum } from '../../api';
import i18next from 'i18next'; // Import the i18next instance
import { useApiClient } from '../../hooks/useApiClient';
import { Badge } from 'react-bootstrap';
import { APP_ENV, canViewReportersPage } from 'src/utils';
import { useReporters } from 'src/hooks/useReporters';
import { FaBell, FaListCheck } from 'react-icons/fa6';
import { IoIosCar } from 'react-icons/io';
import { clearProgress } from '../CarConfiguratorMotornet/persistence';
import { clearSavedFilters } from '../../utils/savedFilters';
import { useConfiguratorState } from '../CarConfiguratorMotornet/store';

type MenuItemType = {
	to?: string;
	label: React.ReactNode;
	icon: JSX.Element;
	subMenu?: MenuItemType[];
	class?: string;
	onClick?: (e: MouseEvent) => void;
};

const BuildInfo = ({ isBrandedHmp }) => {
	const buildInfo = {
		version: process.env.REACT_APP_VERSION || '',
		commit: process.env.REACT_APP_COMMIT_SHA || '',
		buildDate: process.env.REACT_APP_BUILD_DATE || '',
	};

	if (!buildInfo.version) {
		return null;
	}

	return (
		<div
			style={{
				fontSize: '8px',
				color: '#666',
				position: 'absolute',
				left: isBrandedHmp ? undefined : 0,
				right: 0,
				top: isBrandedHmp ? 5 : 70,
				margin: 'auto',
				textAlign: 'center',
			}}
		>
			<div className="me-3">v{buildInfo.version}</div>
		</div>
	);
};

const AppEnvBadge = () => {
	const appEnv = APP_ENV.env;

	if (!APP_ENV.showEnv || !appEnv) {
		return null;
	}

	return (
		<h3 className="app-env">
			<Badge
				bg={appEnv.bg}
				text={appEnv.text}
				className="env-label"
				style={{
					backgroundColor: appEnv.bg === 'success' ? 'green!important' : '',
				}}
			>
				{appEnv.label}
			</Badge>
		</h3>
	);
};

const SideMenu = ({ toggled, handleToggleSidebar }) => {
	const { t } = useTranslation();
	const { user, signOut, userRole, isSalesMaseratiOrVolkswagen, isSalesMaserati, isSalesStellantis, isSalesVolkswagen, isBrandedHmp, userReferencePartnerList, isSalesFacile, isSalesStellantisAndYou, canApproveSegnalatori } = useAuth();
	const menuItems: MenuItemType[] = [];
	const navigate = useNavigate();
	const [, , removeCookie] = useCookies();
	const [businessName, setBusinessName] = useState('');
	const [loadingbusinessName, setLoadingBusinessName] = useState(false);
	const { apiClient, handleApi } = useApiClient();
	const canViewApprovalCount = [
		RoleEnum.ADMIN,
		RoleEnum.MASTER,
		// RoleEnum.BACKOFFICE,
		// RoleEnum.AREA_MANAGER
	].includes(userRole) || canApproveSegnalatori;

	const { approvalCount } = useReporters({ enabled: canViewApprovalCount })
	const showApprovalCount = canViewApprovalCount && approvalCount > 0;

	const canViewMyReporter = useMemo(() => {
		return canViewReportersPage(userRole, userReferencePartnerList) && !isSalesFacile;
	}, [userRole, userReferencePartnerList, isSalesFacile])

	const pathIsVisible = (path: string) => {
		const pathname = window.location.pathname;
		return path === pathname;
	};

	const availableItems = {
		'/wizard': {
			to: `/wizard`,
			label: t('new_wizard'),
			icon: <FaMagic />,
		},
		'/available-vehicles': {
			to: '/available-vehicles',
			label: t('vehicles_list'),
			icon: <Icon name="list" />,
		},
		'/promo-vehicles': {
			to: '/promo-vehicles',
			label: t('promo_vehicles'),
			icon: <Icon name="list" />,
		},
		'/my-stock': {
			to: '/my-stock',
			label: t('my_stock'),
			icon: <Icon name="heart" />,
		},
		'/quicko-vehicles': {
			to: '/quicko-vehicles',
			label: 'Quicko',
			icon: (
				<img
					src="/svg-icons/quicko-logo-q.png"
					alt="Quicko"
					style={{ width: '24px', height: '20px' }}
				/>
			),
			class: 'highlighted-item',
			onClick: () => {
				GTMsendEvent({
					event: 'CTA_quicko_menu',
					evtCat: 'Quicko',
					evtAct: 'click',
					evtLab: 'menu',
				});
			},
		},
		'/wishlist': {
			to: `/wishlist`,
			label: t('favourites'),
			icon: <FaRegHeart />,
		},
		'/quotation-request-list': {
			to: `/quotation-request-list`,
			label: t('estimates'),
			icon: <SiOnlyoffice />,
		},
		'/my-companies': {
			to: '/my-companies',
			label: t('my_companies'),
			icon: <FaBuilding />,
		},
		'/my-reporters': {
			to: '/my-reporters',
			label: t('my_reporters'),
			icon: <FaUsers />,
		},
		'/my-accounts': {
			to: '/my-accounts',
			label: t('my_accounts'),
			icon: <FaAddressBook />,
		},
		'/offers': {
			to: '/offers',
			label: t('offers_list'),
			icon: <Icon name="price" />,
		},
		'/vehicle-offer': {
			to: '/vehicle-offer',
			label: t('create_pdf'),
			icon: <FaFilePdf />,
		},
		'/manage-dealers': {
			to: '/manage-dealers',
			label: t('manage_dealers'),
			icon: <FaUserTie />,
		},
		'/manage-users': {
			to: '/manage-users',
			label: (
				<>
					{t('manage_users')}
					{showApprovalCount && <FaBell className="text-warning ms-2" />}
				</>
			),
			icon: <Icon name="user" />,
		},
		'/import': {
			to: '/import',
			label: t('import'),
			icon: <FaFileImport />,
		},
		'/vehicles/resolve': {
			to: '/vehicles/resolve',
			label: t('motornet_backfill_title', 'Risoluzione veicoli'),
			icon: <FaWrench />,
		},
		'/autobiz': {
			to: '/autobiz',
			label: t('used_car_valuation'),
			icon: <Icon name="car-size" />,
		},
		'/area-academy': {
			to: `/area-academy`,
			label: t('area_academy'),
			class: 'highlighted',
			icon: <FaUniversity />,
		},
		'/area-marketing': {
			to: `/area-marketing`,
			label: t('area_marketing'),
			icon: <BiSolidBusiness />,
		},
		'/my-fleet': {
			to: '/my-fleet',
			label: t('my_fleet'),
			icon: <Icon name="shipping" />,
		},
		'/leads': {
			to: '/leads',
			label: t('my_leads'),
			icon: <Icon name="shipping" />,
		},
		'/orders-and-delivery': {
			to: 'https://ffm-nlt.gestioneflotta.it/login',
			label: t('orders_and_delivery'),
			// label: "ORDINIE E CONSEGNE",
			icon: <Icon name="orders-and-delivery" style={{ marginLeft: '5px' }} />,
			// icon: <img src="/images/orders-and-delivery.png" alt="Orders and Delivery" style={{ width: 20, height: 20 }} />,
		},
		'/my-opportunities': {
			to: '/my-opportunities',
			label: t('my_opportunities'),
			icon: <Icon name="list" />,
		},
		'/my-reports': {
			to: '/my-reports',
			label: t('my_opportunities_list'),
			icon: <Icon name="list" />,
		},
		'/stellantis-quote-requests': {
			to: '/stellantis-quote-requests',
			label: t('stellantis_quote_requests'),
			icon: <Icon name="list" />,
		},
		'/stellantis-quotes': {
			to: '/stellantis-quotes',
			label: t('stellantis_quotes'),
			icon: <Icon name="list" />,
		},
		'/stellantis-quotes-history': {
			to: '/stellantis-quotes-history',
			label: t('stellantis_quotes_history'),
			icon: <Icon name="list" />,
		},
		'/stellantis-quotes-segnalatore': {
			to: '/stellantis-quotes-segnalatore',
			label: t('stellantis_quotes'),
			icon: <Icon name="list" />,
		},
		'/car-configurator-list': {
			to: '/car-configurator-list',
			label: t('car_configurable_list'),
			icon: <Icon name="list" />,
		},
		'/vehicle-association': {
			to: '/vehicle-association',
			label: t('vehicle_associations', { defaultValue: 'Associazione veicoli' }),
			icon: <Icon name="list" />,
		},
		'/my-car-configurations': {
			to: '/my-car-configurations',
			label: t('my_car_configurations', { defaultValue: 'Le mie configurazioni' }),
			icon: <Icon name="list" />,
		},
		'/quotations-history': {
			to: '/quotations-history',
			label: t('quotations_history', { defaultValue: 'Storico preventivi' }),
			icon: <Icon name="list" />,
		},
		'/car-configurator': {
			to: '/car-configurator',
			label: t('car_configurator', { defaultValue: 'Configuratore' }),
			icon: <IoIosCar />,
		},
	};

	if (
		[
			RoleEnum.ADMIN,
			RoleEnum.BACKOFFICE,
			RoleEnum.AREA_MANAGER,
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
			RoleEnum.MASTER,
		].includes(userRole)
	) {
		if (
			process.env.REACT_APP_WIZARD_ENABLED === '1' &&
			!isBrandedHmp &&
			isMarket(Markets.it)
		) {
			menuItems.push(availableItems['/wizard']);
		}
	}

	if (userRole === RoleEnum.FLEET_MANAGER) {
		menuItems.push(
			availableItems['/available-vehicles'],
			availableItems['/wishlist'],
			availableItems['/quotation-request-list'],
			availableItems['/my-fleet']
		);
	} else if (userRole === RoleEnum.SEGNALATORE) {
		menuItems.push(
			availableItems['/available-vehicles'],
			// availableItems['/car-configurator'],
			// ...(isMarket(Markets.it) ? [availableItems['/my-car-configurations']] : []),
			availableItems['/my-accounts'],
			availableItems['/my-reports'],
			availableItems['/stellantis-quotes-segnalatore'],
		);
	} else {
		const vehicleMenu = {
			label: t('vehicles'),
			icon: <FaCar />,
			subMenu: [
				availableItems['/available-vehicles'],
				availableItems['/promo-vehicles'],
				availableItems['/my-stock'],
			],
		};
		if (isMarket(Markets.it)) {
			if ([RoleEnum.MASTER].includes(userRole)) {
				vehicleMenu.subMenu.push(availableItems['/car-configurator-list'])
			}
			if ([RoleEnum.MASTER, RoleEnum.BACKOFFICE].includes(userRole)) {
				vehicleMenu.subMenu.push(availableItems['/vehicle-association'])
			}
			if ([RoleEnum.MASTER].includes(userRole)) {
				vehicleMenu.subMenu.push(
					availableItems['/car-configurator'],
					availableItems['/my-car-configurations'],
				)
			}
		}
		if (userRole === RoleEnum.MASTER && isMarket(Markets.it)) {
			vehicleMenu.subMenu.push(availableItems['/vehicles/resolve']);
		}
		menuItems.push(vehicleMenu);
	}

	if ([RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(userRole)) {
		if (!isBrandedHmp) {
			// menuItems.push(availableItems['/my-companies']);

			if (process.env.REACT_APP_QUICKO_ENABLED === '1') {
				menuItems.push(availableItems['/quicko-vehicles']);
			}
		}
	}

	if (
		[
			RoleEnum.MASTER,
			RoleEnum.ADMIN,
			RoleEnum.BACKOFFICE,
			RoleEnum.AREA_MANAGER,
		].includes(userRole)
	) {
		if (process.env.REACT_APP_QUICKO_ENABLED === '1') {
			menuItems.push(availableItems['/quicko-vehicles']);
		}
	}

	if (
		[
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
			RoleEnum.ADMIN,
			RoleEnum.BACKOFFICE,
			RoleEnum.AREA_MANAGER,
			RoleEnum.MASTER,
		].includes(userRole)
	) {
		const gestioneClientiMenu = {
			label: i18next.t('manage_customers').toUpperCase(),
			// label: t('manager_customers'),
			icon: <FaCar />,
			subMenu: [
				availableItems['/my-accounts'],
				availableItems['/my-opportunities'],
			],
		};

		if ((!isSalesStellantis && !isSalesFacile) || isSalesStellantisAndYou) {
			gestioneClientiMenu.subMenu.push(availableItems['/leads']);
		}


		// Add /my-companies only if the user is not associated with "MASERATI" or "VOLKSWAGEN" or "STELLANTIS"
		if (!isBrandedHmp) {
			gestioneClientiMenu.subMenu.push(availableItems['/my-companies']);
		}

		menuItems.push(gestioneClientiMenu);

		if ([RoleEnum.SALES, RoleEnum.SALES_DEALER].includes(userRole) && isMarket(Markets.it)) {
			menuItems.push(availableItems['/stellantis-quotes']);
		}

		if ([RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(userRole)) {
			menuItems.push(availableItems['/quotations-history']);
		}

		if (canViewMyReporter) {
			menuItems.push(availableItems['/my-reporters']);
		}

		const quotesSubMenu: MenuItemType[] = [];
		if ([RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole) && isMarket(Markets.it)) {
			quotesSubMenu.push(
				availableItems['/stellantis-quote-requests'],
				availableItems['/stellantis-quotes-history'],
			);
		}
		if (userRole === RoleEnum.MASTER) {
			quotesSubMenu.push(availableItems['/quotations-history']);
		}
		if (quotesSubMenu.length) {
			menuItems.push({
				label: i18next.t('quotes').toUpperCase(),
				icon: <FaListCheck />,
				subMenu: quotesSubMenu,
			});
		}
	}

	if (
		[
			RoleEnum.ADMIN,
			RoleEnum.BACKOFFICE,
			RoleEnum.AREA_MANAGER,
			RoleEnum.MASTER,
		].includes(userRole)
	) {
		menuItems.push(availableItems['/offers'], availableItems['/vehicle-offer']);

		if (userRole !== RoleEnum.AREA_MANAGER) {
			menuItems.push(availableItems['/manage-dealers']);
		}

		menuItems.push(availableItems['/manage-users']);
	}

	if ((userRole === RoleEnum.ADMIN || userRole === RoleEnum.MASTER) && isMarket(Markets.it)) {
		menuItems.push(availableItems['/import']);
	}

	// SALES
	if (
		[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
			userRole
		)
	) {
		// menuItems.push(
		// 	availableItems[`/orders-and-delivery`],
		// );
		if (
			!isBrandedHmp &&
			isMarket(Markets.it)
		) {
			menuItems.push(availableItems['/autobiz']);
		}
		menuItems.push(availableItems['/vehicle-offer']);

		if (!isSalesStellantis && !isSalesFacile) {
			menuItems.push(
				availableItems[`/area-academy`],
				availableItems[`/area-marketing`],
			)
		}
	}

	if (
		userRole === RoleEnum.ADMIN ||
		userRole === RoleEnum.BACKOFFICE ||
		userRole === RoleEnum.AREA_MANAGER ||
		userRole === RoleEnum.MASTER
	) {
		// menuItems.push(
		// 	availableItems[`/orders-and-delivery`],
		// );

		if (isMarket(Markets.it)) {
			menuItems.push(availableItems['/autobiz']);
		}
		menuItems.push(
			availableItems[`/area-academy`],
			availableItems[`/area-marketing`]
		);
	}

	const ReactRouterMenuItem = (props: any) => {
		const { to, onClick, label, ...menuItemProps } = props;
		// Check if the 'to' prop is an external URL
		const isExternal = to && /^https?:\/\//.test(to);

		if (isExternal) {
			return (
				<MenuItem
					active={pathIsVisible(to)}
					{...menuItemProps}
					component={
						<a
							href={to}
							onClick={(e) => {
								if (onClick) {
									onClick(e);
								}
							}}
							target="_blank"
							rel="noopener noreferrer"
							aria-label={label || 'External Link'}
						>
							{label}
						</a>
					}
				>
					{props.children}
				</MenuItem>
			);
		}

		return (
			<MenuItem
				active={pathIsVisible(to)}
				{...menuItemProps}
				component={
					<Link
						to={to}
						onClick={(e) => {
							if (onClick) {
								onClick(e);
							}
						}}
						aria-label={label || 'Navigate to page'}
					>
						{label}
					</Link>
				}
			>
				{props.children}
			</MenuItem>
		);
	};

	const ReactRouterSubMenuItem = (props: MenuItemType) => {
		const { icon, label, subMenu } = props;

		const defaultOpen =
			subMenu!.filter((el) => {
				return pathIsVisible(el.to!);
			}).length > 0;

		return (
			//@ts-ignore
			<SubMenu icon={icon} label={label} defaultOpen={defaultOpen}>
				{subMenu!.map((item, index) => (
					<ReactRouterMenuItem
						className={item.class}
						key={index}
						icon={item.icon}
						to={item.to}
						onClick={item.onClick}
					>
						{item.label}
					</ReactRouterMenuItem>
				))}
			</SubMenu>
		);
	};

	const handleSignOut = async (e) => {
		e.preventDefault();
		clearSavedFilters();
		localStorage.removeItem('userRole');
		localStorage.removeItem('business_name');
		useConfiguratorState.getState().reset();
		clearProgress();
		removeCookie(AFFILIATION_KEY, { path: '/' });
		removeCookie(USER_ROLE_KEY, { path: '/' });
		localStorage.removeItem(AFFILIATION_KEY);
		try {
			await signOut();
		} catch (error) {
			console.error('Error during sign-out', error);
		}
		navigate('/');
	};

	const sidebarStyles = {
		'--sidebar-bg-color': isBrandedHmp ? '#0C2340' : '#061128',
		'--sidebar-bg-hover-color': isBrandedHmp ? '#0C2340' : '#061128',
		'--sidebar-color': isBrandedHmp ? '#A1A5A8' : '#adb5bd',
		'--highlight-color': isBrandedHmp ? '#FFFFFF' : '#03ddbc',
		'--highlighted-bg-color': isBrandedHmp ? '#0A1B2C' : '#157499',
		'--highlighted-color': isBrandedHmp ? '#FFFFFF' : '#FFFFFF',
		'--highlight-active-color': isBrandedHmp ? 'red' : '#03ddbc',
		'--highlight-active-bg-color': isBrandedHmp ? '#FFFFFF' : '#061128',
	} as React.CSSProperties;

	const logoByCustom = useMemo(() => {
		if (isSalesVolkswagen) {
			return "/svg-icons/volkswagen-logo-white.png";
		}

		if (isSalesStellantis) {
			return "/svg-icons/stellantis-logo-white.png"
		}

		if (isSalesMaserati) {
			return "/svg-icons/maserati-logo-white.png"
		}

		if (isSalesFacile) {
			return "/svg-icons/facile-logo-white.png"
		}
	}, [isSalesVolkswagen, isSalesStellantis, isSalesMaserati, isSalesFacile]);

	return (
		<Sidebar
			onBackdropClick={() => handleToggleSidebar(true)}
			breakPoint="lg"
			collapsedWidth="0"
			collapsed={toggled}
			toggled={!toggled}
			className="pro-sidebar-v1"
			onBreakPoint={(isBroken) => handleToggleSidebar(isBroken)}
			style={sidebarStyles}
		>
			<Menu className="pro-sidebar-header">
				<MenuItem
					component={<div></div>}
					className="logo-item"
					style={isBrandedHmp ? { height: '100%' } : undefined}
				>
					<BuildInfo isBrandedHmp={isBrandedHmp} />

					<Link to="/">
						{isBrandedHmp
							? (<img className="logo" src={logoByCustom} alt="Logo" />)
							: (<Icon className="logo" name="horizon-light" />)
						}
					</Link>
				</MenuItem>
			</Menu>
			<AppEnvBadge />
			<hr />
			<Menu>
				{menuItems.map((item: MenuItemType, index: number) => {
					return item.subMenu !== undefined ? (
						<ReactRouterSubMenuItem
							key={index}
							icon={item.icon}
							label={item.label}
							subMenu={item.subMenu}
						/>
					) : (
						<ReactRouterMenuItem
							className={item.class}
							key={index}
							icon={item.icon}
							to={item.to}
							onClick={item.onClick}
						>
							{item.label}
						</ReactRouterMenuItem>
					);
				})}
			</Menu>
			<Menu className="username-menu">
				<div className="d-flex flex-column align-items-center p-2">
					<div className="text-center" style={{ wordBreak: 'break-all' }}>
						{user.signInUserSession.accessToken.payload.username}
					</div>
					<div>({_.startCase(_.toLower(userRole))})</div>
				</div>

				{![RoleEnum.FLEET_MANAGER, RoleEnum.SEGNALATORE].includes(userRole) && (
					<ReactRouterMenuItem
						to={`/personal-area/${user.signInUserSession.accessToken.payload.username}`}
						icon={<Icon name="user" />}
					>
						{t('personal_area')}
					</ReactRouterMenuItem>
				)}

				{isMarket(Markets.it) && (
					<ReactRouterMenuItem to="/contacts" icon={<FaHeadset />}>
						{t('contacts_and_support', { defaultValue: 'Contatti e supporto' })}
					</ReactRouterMenuItem>
				)}

				<MenuItem icon={<FaSignOutAlt />} component={<div></div>}>
					<Link to="/" onClick={handleSignOut}>
						{t('logout')}
					</Link>
				</MenuItem>
			</Menu>
			<hr />
			<Menu>
				<MenuItem component={<div></div>}>
					<div className="privacy-links">
						{/* Redirect to privacy-dealer/:title and it is handled in the EmbedFile component */}
						<a
							href={`/privacy-dealer/Informativa Dealer.docx`}
							target="_blank"
							rel="noreferrer"
						>
							{t('privacy_dealer')}
						</a>
						<a
							href={`/privacy-dipendenti/Informativa dipendenti Horizon.docx`}
							target="_blank"
							rel="noreferrer"
						>
							{t('privacy_employees')}
						</a>
					</div>
				</MenuItem>
			</Menu>
		</Sidebar>
	);
};

export default SideMenu;
